import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { ProductCard, ProductGrid, Skeleton } from '@kamalkari/ui';
import { useProductDetailStore } from '@/stores/productDetailStore';
import type { Product, Category } from '@kamalkari/types';
import { KolamDivider } from './KolamPattern';

const API_URL = import.meta.env.VITE_API_URL || 'https://0vkok222yh.execute-api.us-east-1.amazonaws.com';

export function FeaturedProducts() {
  const openProduct = useProductDetailStore((s) => s.openProduct);

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      return data.data?.items || data.items || [];
    },
  });

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products-featured'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/products?featured=true&limit=4`);
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      const items = data.data?.items || data.items || [];
      return items.slice(0, 4);
    },
  });

  return (
    <section className="bg-white py-20 relative">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }} />

      <div className="container-page relative">
        <div className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xs font-medium tracking-[0.3em] uppercase text-ochre mb-4"
          >
            Current Exhibition
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-serif text-warm-900 mb-6"
          >
            Featured Artworks
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <KolamDivider className="w-64 h-8 mx-auto text-warm-400" />
          </motion.div>
        </div>

        {isLoading ? (
          <ProductGrid columns={4}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-warm-50 p-3 rounded-sm">
                <Skeleton className="aspect-[3/4] rounded-sm mb-4" />
                <Skeleton className="h-4 w-2/3 mb-2" />
                <Skeleton className="h-4 w-1/3" />
              </div>
            ))}
          </ProductGrid>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-warm-600">No featured products available.</p>
          </div>
        ) : (
          <ProductGrid columns={4}>
            {products.map((product: Product, index: number) => (
              <motion.div
                key={product.productId}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                {/* Gallery-style frame */}
                <div className="bg-warm-50 p-3 rounded-sm shadow-sm group-hover:shadow-lg transition-shadow duration-300">
                  <ProductCard
                    id={product.productId}
                    name={product.name}
                    price={product.price}
                    compareAtPrice={product.compareAtPrice}
                    image={product.images[0]}
                    category={categories.find((c: Category) => c.categoryId === product.categoryId)?.name}
                    onClick={openProduct}
                  />
                </div>
              </motion.div>
            ))}
          </ProductGrid>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link
            to="/products"
            className="inline-flex items-center gap-3 text-warm-700 hover:text-warm-900 transition-colors group font-medium"
          >
            <span className="border-b border-warm-300 group-hover:border-warm-600 transition-colors pb-1">
              View Full Collection
            </span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
